import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useStore, FREE_SHIPPING_THRESHOLD, formatINR } from "@/context/StoreContext";

const CartDrawer = () => {
  const {
    isCartOpen, setCartOpen, cart, subtotal,
    updateQty, removeFromCart, clearCart,
    totalMrp, mrpDiscount, totalAmount, savings,
    proceedToCheckout, checkoutLoading,
  } = useStore();

  useEffect(() => {
    document.body.style.overflow = isCartOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isCartOpen]);

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="cart-backdrop"
        onClick={() => setCartOpen(false)}
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-500 ${
          isCartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-md flex flex-col bg-[color:var(--vw-bg)] shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isCartOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--vw-beige)]">
          <p className="overline">Your Bag ({cart.length})</p>
          <button data-testid="close-cart-btn" aria-label="Close cart" onClick={() => setCartOpen(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Free shipping progress */}
        <div className="px-6 py-4 border-b border-[color:var(--vw-beige)] bg-[color:var(--vw-bg-alt)]">
          <p className="text-xs font-utility text-[color:var(--vw-ink-soft)]">
            {remaining > 0
              ? <>You're <span className="text-[color:var(--vw-ink)] font-medium">{formatINR(remaining)}</span> away from complimentary shipping.</>
              : <span className="text-[color:var(--vw-success)] font-medium">You've unlocked complimentary shipping.</span>}
          </p>
          <div className="h-[2px] bg-[color:var(--vw-beige-dark)] mt-3 overflow-hidden">
            <div
              data-testid="free-shipping-progress"
              className="h-full bg-[color:var(--vw-ink)] transition-all duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {cart.length === 0 ? (
            <div data-testid="empty-cart" className="h-full flex flex-col items-center justify-center text-center gap-4 pt-24">
              <p className="font-serif-display text-3xl">Your bag is quiet.</p>
              <p className="text-sm text-[color:var(--vw-ink-soft)] max-w-xs">Every considered piece begins here.</p>
              <button
                onClick={() => setCartOpen(false)}
                className="mt-4 overline border-b border-[color:var(--vw-ink)] pb-1 hover:opacity-70"
              >Continue shopping</button>
            </div>
          ) : (
            <ul className="space-y-6">
              {cart.map(item => (
                <li key={item.key} data-testid={`cart-item-${item.handle}`} className="flex gap-4">
                  <div className="w-20 h-28 bg-[color:var(--vw-bg-alt)] overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <Link to={`/product/${item.handle}`} onClick={() => setCartOpen(false)} className="font-serif-display text-lg leading-tight hover:opacity-70">
                        {item.title}
                      </Link>
                      <p className="text-xs font-utility text-[color:var(--vw-ink-soft)] mt-1">Size {item.size}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-[color:var(--vw-beige-dark)]">
                        <button data-testid={`qty-dec-${item.handle}`} aria-label="Decrease" onClick={() => updateQty(item.key, -1)} className="p-2 hover:bg-[color:var(--vw-bg-alt)]"><Minus size={12} /></button>
                        <span className="w-8 text-center text-sm font-utility">{item.quantity}</span>
                        <button data-testid={`qty-inc-${item.handle}`} aria-label="Increase" onClick={() => updateQty(item.key, 1)} className="p-2 hover:bg-[color:var(--vw-bg-alt)]"><Plus size={12} /></button>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-utility font-medium">{formatINR(item.price * item.quantity)}</p>
                        <button data-testid={`remove-${item.handle}`} aria-label="Remove" onClick={() => removeFromCart(item.key)} className="text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)]">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-[color:var(--vw-beige)] px-6 py-6 space-y-4">
            {/* PRICE DETAILS */}
            <div data-testid="price-details" className="space-y-2.5">
              <p className="overline text-[color:var(--vw-ink-soft)]">Price Details</p>

              <div className="flex items-center justify-between text-sm font-utility">
                <span className="text-[color:var(--vw-ink-soft)]">Total MRP</span>
                <span data-testid="price-total-mrp" className="text-[color:var(--vw-ink)]">
                  {formatINR(totalMrp)}
                </span>
              </div>

              {mrpDiscount > 0 && (
                <div className="flex items-center justify-between text-sm font-utility">
                  <span className="text-[color:var(--vw-ink-soft)]">Discount on MRP</span>
                  <span data-testid="price-mrp-discount" className="text-[color:var(--vw-success)]">
                    -{formatINR(mrpDiscount)}
                  </span>
                </div>
              )}

              {/* Subtle divider above Total */}
              <div className="border-t border-[color:var(--vw-beige-dark)]/50 pt-3 mt-1 flex items-baseline justify-between">
                <span className="overline">Total Amount</span>
                <span data-testid="price-total-amount" className="font-serif-display text-2xl">
                  {formatINR(totalAmount)}
                </span>
              </div>

              {savings > 0 && (
                <p data-testid="price-savings" className="text-xs font-utility text-[color:var(--vw-success)] text-right">
                  You saved {formatINR(savings)}
                </p>
              )}
            </div>

            <p className="text-xs text-[color:var(--vw-ink-soft)]">Taxes and shipping calculated at checkout.</p>
            <button
              data-testid="checkout-btn"
              onClick={proceedToCheckout}
              disabled={checkoutLoading}
              className="w-full py-4 overline text-[color:var(--vw-bg)] bg-[color:var(--vw-ink)] hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {checkoutLoading ? "Preparing checkout…" : "Proceed to Checkout"}
            </button>
            <button
              onClick={clearCart}
              className="w-full overline text-[color:var(--vw-ink-soft)] hover:text-[color:var(--vw-ink)]"
            >
              Clear bag
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
